import React, { useContext, useMemo, useState } from "react";

import HtmlEditor from "./HtmlEditorComponents/HtmlEditor";
import CreateNewBanner from "./CreateNewBanner";
import BannerLibrary from "./BannerLibrary";

import ClientSearchSelect from "../../../common/ClientSearchSelect";
import { GlobalContext } from "../../../../Context/GlobalContext";

const DRAFT_KEY = "banner-create-draft";
const GENERATED_HTML_KEY = "banner-generated-html";

const getClientUserId = (client) =>
  String(
    client?.userid ??
      client?.userId ??
      client?.user_id ??
      client?.us_code ??
      client?.code ??
      client?.value ??
      client?.id ??
      "",
  ).trim();

const getClientCode = (client) =>
  String(
    client?.us_code ??
      client?.userid ??
      client?.userId ??
      client?.user_id ??
      client?.code ??
      client?.value ??
      client?.id ??
      "",
  ).trim();

function Banner() {
  const { AllClients = [], loading } = useContext(GlobalContext);

  const [activeBannerTab, setActiveBannerTab] = useState("library");
  const [selectedClient, setSelectedClient] = useState(null);

  const clients = useMemo(() => {
    return (Array.isArray(AllClients) ? AllClients : []).map((client) => ({
      ...client,

      us_code:
        client?.us_code ??
        client?.userid ??
        client?.userId ??
        client?.user_id ??
        client?.value ??
        client?.id ??
        "",

      us_name:
        client?.organization_name ??
        client?.us_name ??
        client?.us_loginid ??
        client?.us_email ??
        client?.name ??
        client?.label ??
        "Client",
    }));
  }, [AllClients]);

  /* =========================================================
     SELECTED CLIENT VALUES
  ========================================================= */

  const selectedUserId = getClientUserId(selectedClient);

  const selectedClientCode = getClientCode(selectedClient);

  const isCreateSection =
    activeBannerTab === "create" || activeBannerTab === "editor";

  /* =========================================================
     CLIENT CHANGE
  ========================================================= */

  const handleClientChange = (client) => {
    const nextUserId = getClientUserId(client);

    if (nextUserId !== selectedUserId) {
      sessionStorage.removeItem(DRAFT_KEY);

      sessionStorage.removeItem(GENERATED_HTML_KEY);
    }

    setSelectedClient(client || null);

    setActiveBannerTab("library");
  };

  const handleOpenLibrary = () => {
    setActiveBannerTab("library");
  };

  const handleOpenCreate = () => {
    if (!selectedUserId) {
      return;
    }

    sessionStorage.removeItem(GENERATED_HTML_KEY);

    setActiveBannerTab("create");
  };

  const handleOpenEditor = () => {
    if (!selectedUserId) {
      return;
    }

    setActiveBannerTab("editor");
  };

  const handleGenerated = () => {
    setActiveBannerTab("create");
  };

  const handleCreated = () => {
    sessionStorage.removeItem(DRAFT_KEY);

    sessionStorage.removeItem(GENERATED_HTML_KEY);

    setActiveBannerTab("library");
  };

  return (
    <div className="w-full montserrat-font-1">

      <div className="mb-2 flex w-full flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold montserrat-font-1">
            Banner Management
          </h2>

          <p className="text-sm text-gray-500 montserrat-font-2">
            Create reusable banner templates that save time and ensure
            consistency
          </p>
        </div>

        {/* CLIENT DROPDOWN */}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            Select Client
          </label>

          <ClientSearchSelect
            clients={clients}
            value={selectedClientCode}
            onChange={handleClientChange}
            placeholder={
              clients.length === 0 ? "No clients found" : "Select client..."
            }
            searchPlaceholder="Search by client name or code..."
            emptyText="No client found"
            noOptionsText="No clients"
            disabled={loading && clients.length === 0}
            className="!w-[320px]"
          />

          {selectedUserId && (
            <p className="text-[11px] font-medium text-[#01C37D]">
              Selected User ID: {selectedUserId}
            </p>
          )}
        </div>
      </div>

      {/* =====================================================
          TABS
      ===================================================== */}

      <div className="mt-2 flex w-full gap-3 border-b">
        {/* LIBRARY */}

        <button
          type="button"
          onClick={handleOpenLibrary}
          className="border-none px-3 py-2 text-sm font-medium"
          style={{
            borderBottom:
              activeBannerTab === "library"
                ? "3px solid #00c47d"
                : "3px solid transparent",

            color: activeBannerTab === "library" ? "#00c47d" : "#111827",
          }}
        >
          Banner Library
        </button>

        {/* CREATE */}

        <button
          type="button"
          disabled={!selectedUserId}
          onClick={handleOpenCreate}
          className="border-none px-3 py-2 text-sm font-medium"
          title={!selectedUserId ? "Please select a client first" : ""}
          style={{
            borderBottom: isCreateSection
              ? "3px solid #00c47d"
              : "3px solid transparent",

            color: isCreateSection
              ? "#00c47d"
              : !selectedUserId
                ? "#9ca3af"
                : "#111827",

            cursor: !selectedUserId ? "not-allowed" : "pointer",
          }}
        >
          Create Banner
        </button>
      </div>

      <div className="mt-3 w-full">
        {/* LIBRARY */}

        {activeBannerTab === "library" && (
          <BannerLibrary
            key={selectedUserId || "no-client"}
            selectedUserId={selectedUserId}
          />
        )}

        {/* CREATE */}

        {activeBannerTab === "create" && (
          <CreateNewBanner
            key={selectedUserId}
            selectedUserId={selectedUserId}
            onOpenEditor={handleOpenEditor}
            onCreated={handleCreated}
            onCancel={handleOpenLibrary}
          />
        )}

        {/* EDITOR */}

        {activeBannerTab === "editor" && (
          <HtmlEditor onGenerated={handleGenerated} />
        )}
      </div>
    </div>
  );
}

export default Banner;
